import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';

interface Profile {
  id: string;
  user_id: string;
  display_name: string | null;
  created_at: string;
}

interface AuthState {
  user: User | null;
  profile: Profile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    profile: null,
    isLoading: true,
    isAuthenticated: false,
  });

  // Fetch user profile
  const fetchProfile = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching profile:', error instanceof Error ? error.message : 'Unknown error');
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error fetching profile:', error instanceof Error ? error.message : 'Unknown error');
      return null;
    }
  }, []);

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          const profile = await fetchProfile(session.user.id);
          setAuthState({
            user: session.user,
            profile,
            isLoading: false,
            isAuthenticated: true,
          });
        } else {
          setAuthState({
            user: null,
            profile: null,
            isLoading: false,
            isAuthenticated: false,
          });
        }
      } catch (error) {
        console.error('Auth init error:', error instanceof Error ? error.message : 'Unknown error');
        setAuthState({
          user: null,
          profile: null,
          isLoading: false,
          isAuthenticated: false,
        });
      }
    };

    initAuth();

    // Listen for auth changes - IMPORTANT: use synchronous callback to avoid deadlocks
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (import.meta.env.DEV) console.log('Auth state change:', event);
        
        if (session?.user) {
          // Synchronously update with user info first
          setAuthState(prev => ({
            ...prev,
            user: session.user,
            isAuthenticated: true,
            isLoading: false,
          }));
          
          // Defer profile fetch to avoid Supabase deadlock
          setTimeout(() => {
            fetchProfile(session.user.id).then(profile => {
              setAuthState(prev => ({
                ...prev,
                profile,
              }));
            });
          }, 0);
        } else {
          setAuthState({
            user: null,
            profile: null,
            isLoading: false,
            isAuthenticated: false,
          });
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  // Sign up
  const signUp = async (email: string, password: string, displayName?: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { name: displayName },
        },
      });

      if (error) throw error;

      // Fire welcome email (idempotent — one send per user thanks to idempotencyKey).
      // Don't block signup on email delivery; queue it and move on.
      // Respect the global toggle in email_template_settings.
      if (data.user?.id) {
        supabase
          .from('email_template_settings')
          .select('enabled')
          .eq('template_name', 'welcome')
          .maybeSingle()
          .then(({ data: setting }) => {
            if (setting && setting.enabled === false) {
              if (import.meta.env.DEV) console.log('Welcome email disabled by admin toggle, skipping.');
              return;
            }
            supabase.functions
              .invoke('send-transactional-email', {
                body: {
                  templateName: 'welcome',
                  recipientEmail: email,
                  idempotencyKey: `welcome-${data.user!.id}`,
                  templateData: { name: displayName },
                },
              })
              .catch((err) => {
                if (import.meta.env.DEV) console.error('Welcome email enqueue failed:', err);
              });
          });
      }

      return { user: data.user, error: null };
    } catch (error) {
      console.error('Sign up error:', error instanceof Error ? error.message : 'Unknown error');
      return { user: null, error: error as Error };
    }
  };

  // Sign in
  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      return { user: data.user, error: null };
    } catch (error) {
      console.error('Sign in error:', error instanceof Error ? error.message : 'Unknown error');
      return { user: null, error: error as Error };
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      setAuthState({
        user: null,
        profile: null,
        isLoading: false,
        isAuthenticated: false,
      });
    } catch (error) {
      console.error('Sign out error:', error instanceof Error ? error.message : 'Unknown error');
    }
  };

  return {
    ...authState,
    signUp,
    signIn,
    signOut,
  };
};
